import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { setFilter } from "src/store/slices/fault.slice";
import { setTCOptionsData } from "src/store/slices/town-council.slice";
import { getFaults } from "../../core/api/services/fault.service";
import ContentHeader from "../../shared/content-header/content-header.component";
import TableSort from "../../shared/helpers/table-sort";
import capitalizeFirst from "../../shared/helpers/upper-case";
import {
  getBlocksBySiteCode,
  setBlockOptionsData,
} from "../../store/slices/location-block.slice";
import {
  getSiteByTCCode,
  setSiteOptionsData,
} from "../../store/slices/location-site.slice";
import FaultTableComponent from "./components/fault-table.component";
import * as moment from "moment";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";

const FaultComponent = () => {
  const dispatch = useDispatch();
  const [PageNumber, setPageNum] = useState<number>(1);
  const [filters, setFilters] = useState<any>({});
  const pageSize = 10;

  const filter = useSelector((state: any) => {
    return state.fault.filter;
  });
  const [processedFaults, setProcessedFaults] = useState<any[]>([]);

  const [SortBy, setSortBy] = useState<any>(capitalizeFirst("timestamp"));
  const [SortDirection, setSortDirection] = useState<any>("desc");
  const thisMonth = moment().format("YYYY-MM");
  const navigate = useNavigate();

  const renderPropForFloorPlanView = (row: any) => {
    const floorplanPath = getFloorplanViewPath(row);
    const isDisabled = !floorplanPath;
    return (
      <button
        type="button"
        className="d-flex align-items-center gap-1"
        title={isDisabled ? "Asset Not Annotated" : "View in Floor Plan"}
        style={{
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.5 : 1,
          color: isDisabled ? "#999" : "inherit",
          padding: "0",
          background: "none",
          border: "none",
        }}
        onClick={() => {
          if (!isDisabled) {
            navigate(floorplanPath);
          }
        }}
      >
        <FontAwesomeIcon icon={faEye} className="fs-7 me-2" />
      </button>
    );
  };

  const [columns, setColumns] = useState([
    {
      title: "Location",
      label: "address",
      type: "link",
      link: "/dashboard",
      linkableField: "blockCode",
      width: "15rem",
    },
    {
      title: "Asset ID",
      label: "assetCode",
      width: "18rem",
      type: "link",
      link: "/asset",
      linkableField: "assetCode",
    },
    {
      title: "Display Name",
      label: "sensorDisplayName",
      width: "13rem",
    },
    {
      title: "Floor Plan",
      label: "floorplanPath",
      renderProps: renderPropForFloorPlanView,
      width: "7rem",
      excludeSort: true,
    },
    {
      title: "Status",
      label: "status",
    },
    {
      title: "Report Date & Time",
      label: "timestamp",
      type: "datetime",
      width: "13rem",
      isSorted: true,
    },
    {
      title: "Fault Type",
      label: "faultType",
      width: "13rem",
    },
    {
      title: "Fault Description",
      label: "faultDescription",
      width: "13rem",
    },

    {
      title: "Acknowledged By",
      label: "acknowledgeBy",
      width: "13rem",
    },
    {
      title: "Acknowledged Time",
      label: "acknowledgeDate",
      type: "datetime",
      width: "13rem",
    },
    {
      title: "Resolved By",
      label: "resolvedBy",
      width: "13rem",
    },
    {
      title: "Resolved Time",
      label: "resolveDate",
      type: "datetime",
      width: "13rem",
    },
    {
      title: "Remarks",
      label: "remarks",
      // width: "12rem",
    },
  ]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tc = urlParams.get("tc");
    const site = urlParams.get("site");
    const block = urlParams.get("block");
    const faultType = urlParams.get("faultType");
    const isAcknowledged = urlParams.get("isAcknowledged");
    const startDate = urlParams.get("startDate");

    if (tc || site || block || faultType) {
      if (tc) {
        dispatch(getSiteByTCCode({ tcCode: tc }));
      }
      if (tc && site) {
        dispatch(getBlocksBySiteCode({ siteCode: site }));
      }
      dispatch(
        setFilter({
          townCouncil: tc || "",
          site: site || "",
          block: block || "",
          faultType: faultType || 0,
          isAcknowledged: isAcknowledged || "3",
          startDate: startDate || thisMonth,
        }),
      );
      refetch();
    }
  }, []);

  useEffect(() => {
    setPageNum(1);
    setFilters(filter);
  }, [filter]);

  useEffect(() => {
    dispatch(setTCOptionsData([]));
    dispatch(setSiteOptionsData([]));
    dispatch(setBlockOptionsData([]));
  }, []);

  const {
    data: faults,
    isLoading: isLoadingFaults,
    refetch,
  } = useQuery({
    queryKey: [
      "getFaults",
      PageNumber,
      filter?.townCouncil,
      filter?.site,
      filter?.block,
      filter?.isAcknowledged,
      SortBy,
      SortDirection,
      filter?.faultType,
      filter?.startDate,
    ],
    queryFn: ({ signal }) => {
        let acknowledgedParam: string;
        if (filter?.isAcknowledged === undefined) {
          acknowledgedParam = "3";
        } else if (filter?.isAcknowledged === "") {
          acknowledgedParam = "0";
        } else {
          acknowledgedParam = filter.isAcknowledged;
        }

        let faultTypeParam: number | string;
        if (filter?.faultType === undefined) {
          faultTypeParam = 0;
        } else if (filter?.faultType === "") {
          faultTypeParam = 0;
        } else {
          faultTypeParam = filter.faultType;
        }

        const startDateParam = filter?.startDate
          ? moment.utc(`${filter.startDate}-01`).format("YYYY-MM-DD")
          : `${thisMonth}-01`;

        return getFaults(
        PageNumber,
        pageSize,
        filter?.townCouncil,
        filter?.site,
        filter?.block,
        startDateParam,
        acknowledgedParam,
        SortBy,
        SortDirection,
        faultTypeParam,
        signal,
      );
    },
  });

  const removeFirstParens = (text?: string): string => {
    if (!text) return "";
    const start = text.indexOf("(");
    if (start === -1) return text;
    const end = text.indexOf(")", start);
    if (end === -1) return text;
    return text.slice(0, start) + text.slice(end + 1).trimStart();
  };

  // inside your component
  useEffect(() => {
    if (faults?.data) {
      const updatedData = faults.data.map((item: any) => ({
        ...item,
        faultDescription: removeFirstParens(item.faultDescription), // Remove text in parentheses
      }));
      setProcessedFaults(updatedData);
    } else {
      setProcessedFaults([]);
    }
  }, [faults?.data]);

  const onRefetchData = () => {
    if (faults?.numberOfRecords <= pageSize + 1) {
      setPageNum(1);
    }
    refetch();
  };

  const handleChangePage = useCallback((page: number): void => {
    setPageNum(page);
  }, []);

  const handleChangeSort = useCallback(
    async (sortBy: string, sortDirection: any) => {
      if (!sortBy || !sortDirection) return;
      setSortBy(sortBy);
      setSortDirection(sortDirection);

      const copyColumns: any = await TableSort(columns, sortBy);
      setColumns(copyColumns);
    },
    [],
  );

  const getFloorplanViewPath = (row: any) => {
    const blockCode = row?.blockCode;
    const blockToFloorCode = row?.blockToFloorCode;
    const assetCode = row?.assetCode;

    if (!blockCode || !blockToFloorCode || !assetCode) {
      return;
    }
    const floorplanPath = `/dashboard/${btoa(
      blockCode,
    )}/floorplan/${encodeURIComponent(blockToFloorCode)}?assetCode=${btoa(
      assetCode,
    )}`;
    return floorplanPath;
  };

  return (
    <>
      <Helmet>
        <title>Fault | SLP</title>
      </Helmet>
      <div>
        <div className="header-container">
          <div className="title-container">
            {/* <BreadcrumbComponent
            breadCrumbInfo={breadCrumbInfo}
          /> */}
            <ContentHeader pageTitle="Fault" />
          </div>
        </div>

        <div className="page">
          <div className="page-container mt-4">
            <FaultTableComponent
              columns={columns}
              data={processedFaults}
              totalNumberOfRecords={faults?.numberOfRecords}
              pageSize={pageSize}
              current={PageNumber}
              onChangePage={handleChangePage}
              onRefetchData={onRefetchData}
              onChangeSort={handleChangeSort}
              isLoading={isLoadingFaults}
              filter={filter}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default FaultComponent;
